import type { CompiledChart } from '../../songpack/index.js';
import { createWave, resetWave, type Wave } from './Wave.js';
import { WaveState, isWaveVisible } from './WaveState.js';

/**
 * Owns the pool of live waves and advances them against song time.
 *
 * The load-bearing decision is that **radius is reconstructed, never
 * accumulated**:
 *
 * ```
 * radius = initialRadius + (songTime - expansionStart) * speed
 * ```
 *
 * Unity computed the same expression from `timeline.Elapsed` rather than by
 * integrating `Time.deltaTime`, and keeping that is what makes a dropped frame
 * harmless. A wave's size is a pure function of the clock, so after a two-second
 * stall every wave is exactly where the music says it should be on the very next
 * frame — no catch-up loop, no drift, nothing to reconcile.
 */

export interface WaveSystemConfig {
  /**
   * Extra radius beyond a wave's own distance from the origin before it retires.
   * Unity hard-codes 10 in `CircularWave.Update`.
   */
  readonly despawnMargin: number;
  /**
   * Cap on concurrently live waves. Unity takes this from the difficulty preset,
   * which Song Pack v1 does not carry, so it is configured here.
   */
  readonly maxSimultaneousWaves: number;
  /** Pool size. Spawns beyond this are dropped and counted. */
  readonly capacity: number;
  /**
   * How long a judged wave keeps its slot while it dissolves, in seconds.
   *
   * The wave is out of play the instant it is judged — it cannot be tapped
   * again and its radius stops moving — but it keeps a pool slot so the view
   * has something to animate. Removing it on the same frame would mean the
   * player's own input is the one thing in the game with no feedback.
   */
  readonly resolveFadeSeconds: number;
}

export const DEFAULT_WAVE_SYSTEM_CONFIG: WaveSystemConfig = Object.freeze({
  despawnMargin: 10,
  maxSimultaneousWaves: 64,
  capacity: 96,
  resolveFadeSeconds: 0.45,
});

export class WaveSystem {
  /** The pool. Iterate and skip inactive entries; never reordered. */
  readonly pool: readonly Wave[];

  private live = 0;
  private spawned = 0;
  private retired = 0;
  private droppedAtCap = 0;
  private peak = 0;
  private nextId = 1;

  constructor(private readonly config: WaveSystemConfig = DEFAULT_WAVE_SYSTEM_CONFIG) {
    const pool: Wave[] = [];
    for (let index = 0; index < config.capacity; index += 1) pool.push(createWave(index));
    this.pool = pool;
  }

  /** Pool slots in use, including waves scheduled but not yet shown. */
  get pooledCount(): number {
    return this.live;
  }

  /**
   * Waves actually on screen.
   *
   * `isWaveVisible` rather than a local test, so this number cannot disagree
   * with what the renderer draws or with what the hit test accepts. All three
   * ask the same function.
   */
  get activeCount(): number {
    let visible = 0;
    for (const wave of this.pool) {
      if (isWaveVisible(wave)) visible += 1;
    }
    return visible;
  }

  /** The waves on screen, in pool order. The set anything inspecting should use. */
  get visibleWaves(): Wave[] {
    return this.pool.filter(isWaveVisible);
  }
  get spawnedCount(): number {
    return this.spawned;
  }
  get retiredCount(): number {
    return this.retired;
  }
  get droppedCount(): number {
    return this.droppedAtCap;
  }
  get peakSimultaneous(): number {
    return this.peak;
  }

  /**
   * Spawns the wave for one chart event.
   *
   * Returns null when the cap is reached. That mirrors Unity, which advances its
   * event index first and only then checks the cap — so an event arriving while
   * the field is full is *dropped*, not deferred. Deferring would push waves off
   * the beat they were authored for, which is worse than losing one.
   */
  spawn(
    chart: CompiledChart,
    eventIndex: number,
    originX: number,
    originY: number,
    songTime: number,
  ): Wave | null {
    if (this.live >= this.config.maxSimultaneousWaves) {
      this.droppedAtCap += 1;
      return null;
    }
    // Plain scan rather than `find`, so a spawn allocates no closure.
    let wave: Wave | undefined;
    for (const candidate of this.pool) {
      if (!candidate.active) {
        wave = candidate;
        break;
      }
    }
    if (wave === undefined) {
      this.droppedAtCap += 1;
      return null;
    }

    const events = chart.events;
    const time = events.time[eventIndex] ?? 0;
    const warningLead = events.warningLeadTime[eventIndex] ?? 0;

    wave.active = true;
    wave.eventIndex = eventIndex;
    wave.spawnTime = time;
    wave.expansionStartTime = time + warningLead;
    wave.originX = originX;
    wave.originY = originY;
    wave.initialRadius = Math.max(0, events.initialRadius[eventIndex] ?? 0);
    // Unity floors speed at 0.1 so a zero-speed event cannot wedge the pool.
    wave.speed = Math.max(0.1, events.speed[eventIndex] ?? 0);
    wave.intensity = events.intensity[eventIndex] ?? 0;
    wave.gateAngle = events.gateAngle[eventIndex] ?? 0;
    wave.gateCenterAngle = events.gateCenterAngle[eventIndex] ?? 0;
    wave.gateWidth = events.gateWidth[eventIndex] ?? 0;
    wave.damage = events.damage[eventIndex] ?? 0;
    wave.baseScore = events.baseScore[eventIndex] ?? 0;
    wave.despawnRadius = this.config.despawnMargin + Math.hypot(originX, originY);
    wave.judgement = null;
    wave.resolvedAt = 0;
    wave.badAttempts = 0;
    wave.lastBadAt = -1e9;
    wave.lockedUntil = -1e9;
    // Overwritten by `applyWaveSpeed` as soon as the caller has a best time;
    // set here so a wave is never briefly readable with another wave's timings.
    wave.bestTime = time;
    wave.visualSpawnTime = time;
    wave.visualExpansionStart = wave.expansionStartTime;
    wave.visualLeadTime = 0;
    wave.visualSpeed = wave.speed;

    copyColor(events.dangerColor, eventIndex, wave.dangerColor);
    copyColor(events.gateColor, eventIndex, wave.gateColor);
    copyColor(events.paintColor, eventIndex, wave.paintColor);

    // Evaluate immediately so a wave spawned during a frame skip is already the
    // right size rather than starting from zero a frame late.
    applyTime(wave, songTime);

    this.live += 1;
    this.spawned += 1;
    this.nextId += 1;
    if (this.live > this.peak) this.peak = this.live;
    return wave;
  }

  /** Live waves that have not yet been judged, i.e. still tappable. */
  get pendingCount(): number {
    let pending = 0;
    for (const wave of this.pool) {
      if (wave.active && wave.judgement === null && wave.state !== WaveState.Pending) pending += 1;
    }
    return pending;
  }

  /** Waves scheduled but not yet visible. Only non-zero below 1× wave speed. */
  get notYetVisibleCount(): number {
    let waiting = 0;
    for (const wave of this.pool) {
      if (wave.active && wave.state === WaveState.Pending) waiting += 1;
    }
    return waiting;
  }

  /**
   * Takes a wave out of play and starts its dissolve.
   *
   * Idempotent by construction: a wave that already carries a judgement is
   * returned untouched, which is the single guard behind "the same wave cannot
   * be resolved twice" — whether the second attempt is a double tap, a tap
   * racing an expiry, or two pointers landing in one frame.
   */
  markResolved(
    wave: Wave,
    judgement: NonNullable<Wave['judgement']>,
    songTime: number,
    fadeSeconds: number,
  ): boolean {
    if (!wave.active || wave.judgement !== null) return false;
    wave.judgement = judgement;
    wave.resolvedAt = songTime;
    wave.fadeSeconds = Math.max(0, fadeSeconds);
    wave.state = WaveState.Resolving;
    return true;
  }

  /**
   * Recomputes every live wave from `songTime`.
   *
   * @param onExpired Called for a wave that reached its despawn radius without
   *   ever being judged. The caller decides what that means — this layer does
   *   not know what a miss is.
   * @param onReleased Called as a wave leaves the pool, judged or not.
   */
  update(
    songTime: number,
    onExpired?: (wave: Wave) => void,
    onReleased?: (wave: Wave) => void,
  ): void {
    for (const wave of this.pool) {
      if (!wave.active) continue;

      if (wave.judgement !== null) {
        // Frozen: age still advances so the view can keep its rotations
        // running, but the radius is whatever it was when the wave was judged.
        wave.age = songTime - wave.spawnTime;
        wave.state = WaveState.Resolving;
        if (songTime - wave.resolvedAt >= wave.fadeSeconds) {
          this.release(wave, onReleased);
        }
        continue;
      }

      applyTime(wave, songTime);
      if (wave.radius > wave.despawnRadius) {
        onExpired?.(wave);
        // Released on the same frame either way. A wave that expires has grown
        // past the play area — it is already off the edge of the picture, so
        // holding it back for a dissolve would animate something nobody can
        // see. The dissolve is feedback for a *tap*, and only a tap earns it.
        this.release(wave, onReleased);
      }
    }
  }

  private release(wave: Wave, onReleased?: (wave: Wave) => void): void {
    wave.state = WaveState.Retired;
    onReleased?.(wave);
    resetWave(wave);
    this.live -= 1;
    this.retired += 1;
  }

  /** Releases every live wave without counting them as retired. */
  clear(): void {
    for (const wave of this.pool) {
      if (wave.active) resetWave(wave);
    }
    this.live = 0;
  }

  resetCounters(): void {
    this.clear();
    this.spawned = 0;
    this.retired = 0;
    this.droppedAtCap = 0;
    this.peak = 0;
  }
}

/**
 * Places a wave at the size the song time implies.
 *
 * Reads the *visual* timeline throughout. At 1× those values are identical to
 * the chart's own — `visualSpawnTime` is `event.time`, `visualExpansionStart`
 * is `event.time + warningLeadTime`, `visualSpeed` is `event.speed` — so the
 * expression below is the same one Unity evaluated. Away from 1× they are the
 * same shape stretched or compressed around `bestTime`, which never moves.
 *
 * Before `visualExpansionStart` the wave exists but has not begun to grow —
 * that is the warning window, and Unity expresses it by returning early from
 * `Update` while the age is negative, leaving the radius at its initial zero.
 */
export function applyTime(wave: Wave, songTime: number): void {
  wave.age = songTime - wave.visualSpawnTime;
  if (wave.age < 0) {
    wave.state = WaveState.Pending;
    wave.radius = 0;
    return;
  }
  const growth = songTime - wave.visualExpansionStart;
  if (growth < 0) {
    wave.state = WaveState.Warning;
    wave.radius = 0;
    return;
  }
  wave.state = WaveState.Expanding;
  wave.radius = wave.initialRadius + growth * wave.visualSpeed;
}

/**
 * How long a wave is shown before the moment it wants to be tapped.
 *
 * The chart's own lead — `event.time` to `bestTime` — is about a second on the
 * real pack, and playing it showed that is not enough. Mandalizm puts six rings
 * of Devanagari, a timing arc and up to fifty overlapping seals on screen at
 * once; a second is barely time to *notice* a wave, let alone read it and act.
 * So the display lead is now set here rather than inherited from the chart, and
 * the chart's lead becomes what it always was — a fact about the music, not a
 * reaction budget.
 *
 * None of this is a judging window. It changes when a wave becomes visible and
 * how fast it grows, and nothing else.
 */
export interface VisualLeadSettings {
  /** Seconds from a wave appearing to its best moment, at 1× wave speed. */
  readonly baseSeconds: number;
  /**
   * The shortest lead any speed setting may produce.
   *
   * At 2× the formula alone gives 0.875 s, which is under the time it takes to
   * find a new seal in a crowded frame. The floor means the speed control
   * trades *density* for reaction time only until reaction time runs out.
   */
  readonly minimumSeconds: number;
  /**
   * Share of the lead spent in the warning window, before the seal grows.
   *
   * A fraction rather than the chart's fixed 0.8 s, so the shape of the
   * approach is the same at every speed instead of the warning eating the whole
   * lead at 2×.
   */
  readonly warningFraction: number;
}

export const DEFAULT_VISUAL_LEAD: VisualLeadSettings = Object.freeze({
  baseSeconds: 1.75,
  minimumSeconds: 1.0,
  warningFraction: 0.22,
});

/** The display lead for a wave speed. Pure, so a test can pin the table. */
export function visualLeadFor(
  multiplier: number,
  settings: VisualLeadSettings = DEFAULT_VISUAL_LEAD,
): number {
  const speed = Math.max(0.05, multiplier);
  return Math.max(settings.minimumSeconds, settings.baseSeconds / speed);
}

/**
 * Rebuilds a wave's visual timeline.
 *
 * Everything is measured backwards from `bestTime`, which is the one value in
 * the wave that this function is forbidden to touch.
 *
 * The expansion rate is scaled by `base / lead` rather than by the multiplier,
 * and that has a property worth stating: the radius a seal has reached *at its
 * best moment* works out to `base × (1 - warningFraction) × event.speed`, with
 * the lead cancelling out. So the figure is the same size when it matters at
 * every wave speed — the speed setting changes how long you watch it grow, not
 * how much of it you get to read.
 */
export function applyWaveSpeed(
  wave: Wave,
  multiplier: number,
  songTime: number,
  settings: VisualLeadSettings = DEFAULT_VISUAL_LEAD,
): void {
  const lead = visualLeadFor(multiplier, settings);
  wave.visualLeadTime = lead;
  wave.visualSpawnTime = wave.bestTime - lead;
  wave.visualExpansionStart = wave.visualSpawnTime + lead * settings.warningFraction;
  wave.visualSpeed = Math.max(0.1, wave.speed * (settings.baseSeconds / lead));
  applyTime(wave, songTime);
}

function copyColor(source: Float32Array, index: number, target: Float32Array): void {
  const base = index * 4;
  target[0] = source[base] ?? 0;
  target[1] = source[base + 1] ?? 0;
  target[2] = source[base + 2] ?? 0;
  target[3] = source[base + 3] ?? 0;
}
